<template>
  <section class="grow overflow-y-auto">
    <ul class="flex flex-col gap-y-2">
      <!-- Innovadis custom: inline catalog first-level categories -->
      <li v-if="catalogMenuItems">
        <ul class="flex flex-col gap-y-2">
          <li v-for="cat in catalogMenuItems" :key="cat.id || cat.title">
            <InnoMobileMenuLink :link="cat" class="py-1 font-bold" @click="$emit('selectItem', cat)">
              <div class="flex items-center">
                <div class="bg-white mr-4 rounded-lg">
                  <VcImage :src="cat.imgSrc" class="size-12 p-1" />
                </div>
                <span class="font-semibold">{{ cat.title }}</span>
              </div>
            </InnoMobileMenuLink>
          </li>
        </ul>
      </li>
    </ul>

    <ul class="divide-y divide-additional-50 divide-opacity-20 mt-4">
      <li v-for="item in mainMenuItems" :key="item.title" class="py-1">
        <InnoMobileMenuLink
          :icon="checkParams(item.route as string)"
          :link="item"
          class="py-1 font-semibold"
          @click="$emit('close')"
        >
          {{ item.title }}
        </InnoMobileMenuLink>
      </li>

      <li class="py-1" v-if="mobileAccountMenuItem && isAuthenticated">
        <InnoMobileMenuLink
          icon="custom-user"
          :link="mobileAccountMenuItem"
          @select="$emit('selectItem', mobileAccountMenuItem!)"
        >
          {{ mobileAccountMenuItem.title }}
        </InnoMobileMenuLink>
      </li>

      <li v-if="!isAuthenticated" v-for="item in unauthorizedMenuItems" :key="item.title">
        <InnoMobileMenuLink :key="item.title" :link="item" class="py-3" @close="$emit('close')">
          {{ item.title }}
        </InnoMobileMenuLink>
      </li>

      <li class="py-3" v-if="loginOnBehalf">
        <router-link
          :to="{ name: 'SignInAdministrator' }"
          @click="$emit('close')"
          class="flex items-center justify-between"
        >
          {{ $t("shared.account.sign_in_form.impersonate.switch_user") }}

          <VcIcon :size="16" class="ml-auto fill-[--mobile-menu-navigation-color]" name="chevron-right-small" />
        </router-link>
      </li>

      <li v-for="item in topHeaderList" :key="item.title" class="py-1">
        <InnoMobileMenuLink :icon="checkParams(item.route as string)" :link="item" class="py-1" @click="$emit('close')">
          {{ item.title }}
        </InnoMobileMenuLink>
      </li>

      <li class="py-3" v-if="isAuthenticated">
        <div @click="signMeOut" class="flex items-center justify-between">
          <VcIcon :size="20" class="mr-3 fill-[--mobile-menu-navigation-color]" name="sign-out" />

          {{ $t("shared.layout.header.link_logout") }}

          <VcIcon :size="16" class="ml-auto fill-[--mobile-menu-navigation-color]" name="chevron-right-small" />
        </div>
      </li>
    </ul>
  </section>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrency, useNavigations } from "@/core/composables";
import { useSignMeOut, useUser } from "@/shared/account";
import type { ExtendedMenuLinkType } from "@/core/types";
import InnoMobileMenuLink from "@/shared/layout/components/header/_internal/mobile-menu/inno-mobile-menu-link.vue";
import { computedEager } from "@vueuse/core";
import { VirtoPermissions, CustomPermissions } from "@/core/enums";

interface IProps {
  menuItem: ExtendedMenuLinkType;
}

interface IEmits {
  (event: "close"): void;
  (event: "selectItem", item: ExtendedMenuLinkType): void;
}

defineEmits<IEmits>();
defineProps<IProps>();

const { signMeOut } = useSignMeOut();
const { user, checkPermissions, isMultiOrganization, isAuthenticated } = useUser();
const { topHeaderList, catalogMenuItems, mainMenuItems } = useNavigations();
const { t } = useI18n();

const loginOnBehalf = computedEager<boolean>(() => checkPermissions(VirtoPermissions.LoginOnBehalf));
const showOrderHistory = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowOrderHistory));
const showInvoiceHistory = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowInvoiceHistory));
const showUserManagement = computedEager<boolean>(() => checkPermissions(CustomPermissions.ShowUserManagement));

const PROSPECT = "Prospect";
const userIsProspect = computed<boolean>(
  () => !!user.value?.roles?.filter((role) => role.name === PROSPECT)[0] || false,
);

const unauthorizedMenuItems: ExtendedMenuLinkType[] = [
  { route: { name: "SignIn" }, title: t("shared.layout.header.link_sign_in") },
  { route: { name: "SignUp" }, title: t("shared.layout.header.link_register_now") },
];

// Check if tws_type contains new or promotion
function checkParams(url: string) {
  const queryString = url.split("?")[1];
  if (!queryString) return null;

  const params = new URLSearchParams(queryString);
  const facets = params.get("facets");

  if (!facets) return null;

  try {
    const json = JSON.parse(`{${facets}}`);
    return json.tws_type ?? null;
  } catch {
    return null;
  }
}

const mobileAccountMenuItem = computed<ExtendedMenuLinkType>(() => {
  return {
    id: "account",
    title: t("shared.layout.header.mobile.my_account"),
    icon: "user-circle",
    children: [
      {
        route: { name: "Profile" },
        title: t("shared.layout.header.mobile.account_menu.profile"),
        icon: "profile",
      },
      ...(isMultiOrganization.value
        ? [
            {
              route: { name: "DropOffPoint" },
              title: t("shared.account.navigation.links.drop_off_point"),
              icon: "truck",
            },
          ]
        : []),
      {
        route: { name: "ChangePasswordAccount" },
        title: t("shared.layout.header.mobile.account_menu.change_password"),
        icon: "pass",
      },
      ...(showOrderHistory.value
        ? [
            {
              route: { name: "Orders" },
              title: t("shared.layout.header.mobile.account_menu.orders"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(showOrderHistory.value
        ? [
            {
              route: { name: "Backorders" },
              title: t("shared.layout.header.mobile.account_menu.backorders"),
              icon: "clipboard-list",
            },
          ]
        : []),
      ...(showInvoiceHistory.value
        ? [
            {
              route: { name: "Invoices" },
              title: t("shared.layout.header.mobile.account_menu.invoices"),
              icon: "clipboard-list",
            },
          ]
        : []),
      {
        route: { name: "Lists" },
        title: t("shared.layout.header.mobile.account_menu.lists"),
        icon: "list-v2",
      },
      {
        route: { name: "CompanyInfo" },
        title: t("shared.layout.header.mobile.corporate_menu.company_info"),
        icon: "office-building",
      },
      ...(showUserManagement.value
        ? [
            {
              route: { name: "CompanyMembers" },
              title: t("shared.layout.header.mobile.corporate_menu.company_members"),
              icon: "user-group",
            },
          ]
        : []),
    ],
  };
});
</script>
